/**
 * useReconciliationNew Hook
 * Main hook for managing reconciliation data and operations
 */

import { useState, useCallback, useMemo } from 'react';
import { mutate as globalMutate } from 'swr';
import { useSelector } from 'react-redux';
import {
  useQueryStatements,
  useQueryStatementTransactions,
  useQueryReconciliationStats,
  getStatementTransactions,
  uploadStatement,
  performReconciliation,
  deleteStatement,
  findMatchingTransactions,
  removeReconciliation,
  unreconcileAllByStatement,
} from '../../../services/api/reconciliationNewService';
import { useQueryEntriesReconciliation } from '../../../services/api/entriesService';
import { toastNotification } from '../../../shared/toastNotification';
import { isReconciled } from '../../../helpers/reconciliationHelpers';

export const useReconciliationNew = () => {
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [selectedJournalEntries, setSelectedJournalEntries] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Unified filter that controls both transaction lists
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('entrada'); // 'entrada', 'saida'

  // Reconciliation status filter - controls both transaction and journal entry lists
  const [reconciliationFilter, setReconciliationFilter] = useState('unreconciled'); // 'all', 'unreconciled', 'reconciled'

  // Get company context from Redux
  const companyId =
    useSelector((state) => state.auth?.companyId) || localStorage.getItem('companyId');

  // Main data queries
  const {
    statements,
    isLoading: isLoadingStatements,
    mutate: mutateStatements,
  } = useQueryStatements(companyId, { enabled: !!companyId });

  const {
    transactions,
    pagination: transactionsPagination,
    isLoading: isLoadingTransactions,
    mutate: mutateTransactions,
  } = useQueryStatementTransactions(selectedStatement?.id, {
    enabled: !!selectedStatement?.id,
    limit: 10000, // Fetch all transactions by using a very high limit
    page: 1,
    // Don't pass isReconciled parameter to get ALL transactions (reconciled + unreconciled)
    // This allows the frontend filter to work properly
  });

  const {
    stats,
    isLoading: isLoadingStats,
    mutate: mutateStats,
  } = useQueryReconciliationStats(companyId, null, {
    enabled: !!companyId,
  });

  // Enhanced date range calculation with buffer protection
  const dateRange = useMemo(() => {
    if (!selectedStatement) return null;

    try {
      // Method 1: Try to get dates from statement metadata if available
      let startDate = null;
      let endDate = null;

      if (selectedStatement.periodStart && selectedStatement.periodEnd) {
        startDate = new Date(selectedStatement.periodStart);
        endDate = new Date(selectedStatement.periodEnd);
      }

      // Method 2: Extract from transactions if no period dates or if we need to verify
      // This is more reliable as it uses actual transaction dates
      if ((!startDate || !endDate) && transactions && transactions.length > 0) {
        const transactionDates = transactions
          .map((t) => t.datePosted)
          .filter((date) => date)
          .map((date) => new Date(date))
          .filter((date) => !isNaN(date.getTime()))
          .sort((a, b) => a - b);

        if (transactionDates.length > 0) {
          startDate = transactionDates[0];
          endDate = transactionDates[transactionDates.length - 1];
          console.log('[useReconciliationNew] Using transaction date range:', {
            startDate: startDate.toISOString().substring(0, 10),
            endDate: endDate.toISOString().substring(0, 10),
            totalTransactions: transactionDates.length,
          });
        }
      }

      // Method 3: Fallback to statement metadata dates if available
      if (
        (!startDate || !endDate) &&
        selectedStatement.statementStartDate &&
        selectedStatement.statementEndDate
      ) {
        startDate = new Date(selectedStatement.statementStartDate);
        endDate = new Date(selectedStatement.statementEndDate);
        console.log('[useReconciliationNew] Using statement metadata dates:', {
          startDate,
          endDate,
        });
      }

      // Add buffer period (±7 days) for better reconciliation coverage
      // This ensures we capture journal entries that might be slightly outside the statement period
      if (startDate && endDate) {
        const bufferDays = 7;
        const bufferedStart = new Date(startDate);
        bufferedStart.setDate(bufferedStart.getDate() - bufferDays);

        const bufferedEnd = new Date(endDate);
        bufferedEnd.setDate(bufferedEnd.getDate() + bufferDays);

        const finalDateRange = {
          startDate: bufferedStart.toISOString().substring(0, 10),
          endDate: bufferedEnd.toISOString().substring(0, 10),
          originalStart: startDate.toISOString().substring(0, 10),
          originalEnd: endDate.toISOString().substring(0, 10),
          bufferDays,
        };

        return finalDateRange;
      }

      return null;
    } catch (error) {
      console.error('[useReconciliationNew] Error calculating date range:', error);
      return null;
    }
  }, [selectedStatement, transactions]);

  const {
    data: journalEntriesData,
    isLoading: isLoadingJournalEntries,
    mutate: mutateJournalEntries,
  } = useQueryEntriesReconciliation(
    companyId,
    dateRange?.startDate,
    dateRange?.endDate,
    'dateVencimento', // Use dateVencimento for reconciliation filtering
    {
      enabled: !!selectedStatement && !!dateRange,
      revalidateIfStale: true, // Enable revalidation for fresh reconciliation data
      revalidateOnFocus: true, // Revalidate when user returns to tab
      revalidateOnReconnect: true, // Revalidate on reconnect
      dedupingInterval: 1000, // Reduce deduping interval to allow more frequent updates
    }
  );

  const statementMonths = useMemo(() => {
    if (!transactions || transactions.length === 0) return new Set();

    const months = new Set();
    transactions.forEach((txn) => {
      if (!txn?.datePosted) return;
      const date = new Date(txn.datePosted);
      if (Number.isNaN(date.getTime())) return;
      months.add(`${date.getUTCFullYear()}-${date.getUTCMonth()}`);
    });

    return months;
  }, [transactions]);

  // Filter journal entries by bank account after fetching
  const journalEntries = useMemo(() => {
    if (!journalEntriesData?.data || !selectedStatement?.bankAccountId) {
      return [];
    }

    console.log('[useReconciliationNew] Processing journal entries from API:', {
      totalFromAPI: journalEntriesData.data.length,
      selectedBankAccountId: selectedStatement.bankAccountId,
      dateRange: dateRange ? `${dateRange.startDate} to ${dateRange.endDate}` : 'No range',
    });

    // Show sample of first few entries
    if (journalEntriesData.data.length > 0) {
      console.log(
        '[useReconciliationNew] Sample entries from API:',
        journalEntriesData.data.slice(0, 3).map((entry) => ({
          id: entry.id,
          description: entry.description?.substring(0, 30),
          bankAccountId: entry.bankAccountId,
          reconciliationData: entry.reconciliationData,
          isInput: entry.isInput,
          journalEntryType: entry.journalEntryType,
          value: entry.value,
        }))
      );
    }

    // Filter by bank account - show ALL entries (reconciled + unreconciled)
    // The reconciliation status filtering will be handled in the TransactionMatchingPanel
    const filteredEntries = journalEntriesData.data.filter((entry) => {
      // Only include transactions and transfers (journalEntryType 1 and 5)
      const isValidEntryType = entry.journalEntryType === 1 || entry.journalEntryType === 5;

      // Filter by bank account
      const matchesBankAccount = entry.bankAccountId === selectedStatement.bankAccountId;

      // Filter by months present in the statement transactions
      let matchesStatementMonth = true;
      if (statementMonths.size > 0) {
        const entryDateRaw = entry.dateVencimento || entry.dateCompetencia;
        const entryDate = entryDateRaw ? new Date(entryDateRaw) : null;
        if (!entryDate || Number.isNaN(entryDate.getTime())) {
          matchesStatementMonth = false;
        } else {
          const key = `${entryDate.getUTCFullYear()}-${entryDate.getUTCMonth()}`;
          matchesStatementMonth = statementMonths.has(key);
        }
      }

      // Return all entries matching the filters (both reconciled and unreconciled)
      // The showOnlyUnreconciled filter will be applied in TransactionMatchingPanel
      return isValidEntryType && matchesBankAccount && matchesStatementMonth;
    });

    // Count reconciled vs unreconciled in the filtered results
    const reconciledCount = filteredEntries.filter(
      (entry) => entry.reconciliationData && entry.reconciliationData.reconciliationId
    ).length;

    console.log('[useReconciliationNew] Filtered entries result:', {
      totalFromAPI: journalEntriesData.data.length,
      afterFiltering: filteredEntries.length,
      reconciledCount,
      unreconciledCount: filteredEntries.length - reconciledCount,
      bankAccountFilter: selectedStatement.bankAccountId,
    });

    return filteredEntries;
  }, [journalEntriesData, selectedStatement?.bankAccountId, dateRange, statementMonths]);

  // Upload file operation
  const handleFileUpload = useCallback(
    async (file, bankAccountId) => {
      if (!file || !bankAccountId || !companyId) {
        throw new Error('File, bank account, and company are required');
      }

      setIsUploading(true);
      setUploadProgress(0);

      try {
        const formData = new FormData();
        formData.append('ofxFile', file);
        formData.append('bankAccountId', bankAccountId);
        formData.append('companyId', companyId);
        formData.append('userId', localStorage.getItem('userId') || '');

        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90));
        }, 200);

        const response = await uploadStatement(formData);

        clearInterval(progressInterval);
        setUploadProgress(100);

        if (response.data?.success) {
          toastNotification(
            'success',
            `Extrato processado com sucesso! ${response.data.newTransactions} novas transações encontradas.`
          );

          // Refresh data
          await mutateStatements();
          await mutateStats();

          // Auto-select the newly uploaded statement
          if (response.data.statement) {
            setSelectedStatement(response.data.statement);
          }

          return response.data;
        } else {
          throw new Error(response.data?.message || 'Erro ao processar extrato');
        }
      } catch (error) {
        console.error('Upload error:', error);
        console.error('Error data:', error.data);
        console.error('Error status:', error.status);
        console.error('Full error object:', error);

        // Normalize response object to detect duplicate/duplicate-like errors reliably
        const resp = error.response || error;
        const responseData = resp?.data || error.data || {};

        const isDuplicate =
          resp?.status === 409 ||
          responseData?.errorType === 'duplicate_file' ||
          /duplicate|already processed|arquivo.*processad|já foi processad|extrato.*exist/i.test(
            (responseData?.message || error?.message || '')
          );

        if (isDuplicate) {
          // Show a concise, user-friendly message for duplicate uploads
          toastNotification('error', 'Extrato já existente', { title: 'Arquivo Já Processado' });
          throw error;
        }

        // Enhanced error handling with specific user-friendly messages based on backend errorType
        let errorMessage = 'Erro inesperado ao processar o arquivo';
        let errorTitle = 'Erro no Upload';

        // Helper to map backend/english messages to Portuguese defaults
        const mapMessageToPt = (msg) => {
          if (!msg) return 'Erro ao processar o extrato.';
          const m = String(msg).toLowerCase();
          if (m.includes('connection') || m.includes('network') || m.includes('connect'))
            return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
          if (m.includes('timeout') || m.includes('timed out')) return 'Tempo de conexão esgotado.';
          if (m.includes('invalid') || m.includes('inválid') || m.includes('invalid_ofx'))
            return 'Dados do arquivo inválidos. Verifique o formato do extrato.';
          if (m.includes('duplicate') || m.includes('already exists') || m.includes('exists'))
            return 'Extrato já existente';
          if (m.includes('unauthorized') || m.includes('forbidden'))
            return 'Acesso negado. Verifique suas permissões.';
          // Fallback generic Portuguese message
          return 'Erro ao processar o extrato.';
        };

        // Since request wrapper rejects with error.response,
        // the error object IS the axios response object
        if (responseData) {
          console.log('Processing error response data:', responseData);

          // Use the errorType from backend for precise error handling
          switch (responseData.errorType) {
            case 'invalid_ofx':
              errorTitle = 'Arquivo OFX Inválido';
              errorMessage =
                'Arquivo OFX inválido ou corrompido. Verifique se é um arquivo .ofx válido do seu banco.';
              break;

            case 'file_error':
              errorTitle = 'Erro no Arquivo';
              errorMessage =
                'Erro ao processar o arquivo. Verifique se o arquivo não está corrompido.';
              break;

            case 'invalid_bank_account':
              errorTitle = 'Conta Bancária Inválida';
              errorMessage =
                'Conta bancária não encontrada ou inválida. Verifique se a conta está cadastrada no sistema.';
              break;

            default:
              // Fallback to a Portuguese message derived from backend message or status
              if (responseData.message) {
                errorMessage = mapMessageToPt(responseData.message);
                // Set appropriate title based on status code
                switch (resp?.status) {
                  case 409:
                    errorTitle = 'Conflito';
                    break;
                  case 422:
                    errorTitle = 'Dados Inválidos';
                    break;
                  case 404:
                    errorTitle = 'Não Encontrado';
                    break;
                  case 400:
                    errorTitle = 'Requisição Inválida';
                    break;
                  default:
                    errorTitle = 'Erro no Upload';
                }
              }
          }
        } else {
          // Handle cases where there's no response data (network errors, etc.)
          switch (resp?.status) {
            case 413:
              errorTitle = 'Arquivo Muito Grande';
              errorMessage =
                'O arquivo é muito grande para ser processado. Tente enviar um extrato com período menor.';
              break;
            case 500:
              errorTitle = 'Erro do Servidor';
              errorMessage =
                'Erro interno do servidor. Tente novamente em alguns instantes ou contate o suporte.';
              break;
            default:
              // This handles cases where error has no status (network errors)
              if (!resp?.status && !responseData) {
                errorTitle = 'Erro de Conexão';
                errorMessage =
                  'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
              } else {
                // Fallback for other error types
                errorMessage = mapMessageToPt(responseData?.message || resp?.statusText || '');
              }
          }
        }

        // Compose a clearer Portuguese message for the user.
        // If backend provided a specific message, prefer a translated/clear version
        // Try to extract any raw backend message from several common fields
        const backendMsgRaw =
          responseData?.message || responseData?.error || responseData?.errors?.[0]?.message ||
          resp?.statusText || error?.message || '';

        // Always map backend messages to Portuguese for the toast. Keep the original in logs only.
        const mapped = backendMsgRaw ? mapMessageToPt(backendMsgRaw) : null;
        console.debug('[useReconciliationNew] Backend error message (raw):', backendMsgRaw);

        // Compose a clear Portuguese message for the user without exposing HTTP status codes.
        let finalMessage;
        if (mapped && mapped !== 'Erro ao processar o extrato.') {
          finalMessage = mapped;
        } else if (backendMsgRaw) {
          // No good translation available, show a clear Portuguese guidance (no status code exposed)
          finalMessage = 'Não foi possível processar o extrato. Verifique o arquivo e tente novamente.';
        } else {
          finalMessage = errorMessage || 'Não foi possível processar o extrato.';
        }

        toastNotification('error', finalMessage, { title: errorTitle });
        throw error;
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [companyId, mutateStatements, mutateStats]
  );

  // Reconciliation operations
  const handleReconciliation = useCallback(
    async (reconciliationPairs, options = {}) => {
      const { allowMultipleTransactions = false } = options;
      if (!reconciliationPairs?.length) {
        throw new Error('Selecione transações para Conciliar');
      }

      try {
        // Validate reconciliation pairs before sending
        const validPairs = reconciliationPairs.filter((pair) => {
          const hasTransactionHash = pair.transaction?.transactionHash;
          const hasJournalEntryId = pair.journalEntry?.id;

          if (!hasTransactionHash) {
            console.warn('[useReconciliationNew] Missing transactionHash for pair:', pair);
          }
          if (!hasJournalEntryId) {
            console.warn('[useReconciliationNew] Missing journalEntryId for pair:', pair);
          }

          return hasTransactionHash && hasJournalEntryId;
        });

        if (validPairs.length === 0) {
          throw new Error(
            'Nenhum par de conciliação válido encontrado. Verifique se as transações e lançamentos estão selecionados corretamente.'
          );
        }

        if (validPairs.length !== reconciliationPairs.length) {
          console.warn(
            `[useReconciliationNew] Filtered ${
              reconciliationPairs.length - validPairs.length
            } invalid pairs`
          );
        }

        const uniqueTransactionHashes = new Set(
          validPairs.map((pair) => pair.transaction.transactionHash)
        );
        if (!allowMultipleTransactions && uniqueTransactionHashes.size > 1) {
          toastNotification(
            'warning',
            'Conciliação bloqueada: selecione apenas uma transação do extrato por conciliação.'
          );
          throw new Error('Multiple bank transactions selected for reconciliation');
        }

        const reconciliationData = {
          reconciliations: validPairs.map((pair) => ({
            id: pair.id || pair.reconciliationGroupId,
            transactionHash: pair.transaction.transactionHash,
            journalEntryId: pair.journalEntry.id,
            reconciliationGroupId: pair.reconciliationGroupId,
            notes: pair.notes || '',
          })),
          userId: localStorage.getItem('userId') || '',
        };

        console.log('[useReconciliationNew] Sending reconciliation data:', {
          reconciliationsCount: reconciliationData.reconciliations.length,
          firstReconciliation: reconciliationData.reconciliations[0],
          userId: reconciliationData.userId,
          allReconciliations: reconciliationData.reconciliations,
        });

        const response = await performReconciliation(reconciliationData);

        if (response.data?.success) {
          const { data: resultData } = response.data;

          if (!resultData?.reconciledCount) {
            const firstError = resultData?.details?.find((detail) => !detail.success);
            toastNotification(
              'warning',
              firstError?.error || 'Nenhum item conciliado. Verifique os valores e datas.'
            );
            return response.data;
          }

          toastNotification(
            'success',
            `Conciliação concluída! ${resultData.reconciledCount} itens conciliados com sucesso.`
          );

          if (resultData.failedCount > 0) {
            toastNotification(
              'warning',
              `${resultData.failedCount} item(ns) não foram conciliados. Verifique os detalhes.`
            );
          }

          // Refresh all relevant data with explicit cache invalidation
          console.log('[useReconciliationNew] Refreshing data after successful reconciliation...');

          // Force complete cache invalidation and revalidation for ALL related data
          await Promise.all([
            mutateTransactions(null, { revalidate: true }), // Force fresh fetch of transactions
            mutateJournalEntries(null, { revalidate: true }), // Force fresh fetch of journal entries
            mutateStats(null, { revalidate: true }), // Force fresh fetch of reconciliation stats
            mutateStatements(null, { revalidate: true }), // Force fresh fetch of statements
            globalMutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-')), // Invalidate bank carousel
          ]);

          // Additional delay to ensure backend has processed the changes
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Second revalidation to ensure we have the latest data
          await Promise.all([
            mutateTransactions(), // Revalidate transactions
            mutateJournalEntries(), // Revalidate journal entries
            mutateStats(), // Revalidate stats
            mutateStatements(), // Revalidate statements
            globalMutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-')), // Refresh bank carousel
          ]);

          console.log(
            '[useReconciliationNew] Data refresh completed with forced cache invalidation'
          );

          // Clear selections
          setSelectedTransactions([]);
          setSelectedJournalEntries([]);

          return response.data;
        } else {
          throw new Error(response.data?.message || 'Erro na conciliação');
        }
      } catch (error) {
        console.error('Reconciliation error:', error);
        console.error('Reconciliation error response:', error.response?.data);
        console.error('Reconciliation error status:', error.response?.status);
        console.error('Reconciliation error full response:', error.response);

        let errorMessage = 'Erro inesperado durante a conciliação';
        let errorTitle = 'Erro na Conciliação';

        // Handle different error structures
        if (error.response?.data) {
          // API error response
          const errorData = error.response.data;
          errorMessage = errorData.message || errorMessage;

          if (error.response.status === 400) {
            errorTitle = 'Dados Inválidos';
            errorMessage =
              errorData.message ||
              'Os dados fornecidos para conciliação são inválidos. Verifique as seleções.';
          } else if (error.response.status === 404) {
            errorTitle = 'Dados Não Encontrados';
            errorMessage =
              'Uma ou mais transações selecionadas não foram encontradas. Atualize a página e tente novamente.';
          } else if (error.response.status === 409) {
            errorTitle = 'Conflito de Conciliação';
            errorMessage =
              'Uma ou mais transações já foram reconciliadas. Atualize os dados e tente novamente.';
          }
        } else if (error.data?.message) {
          // Direct API response error
          errorMessage = error.data.message;
        } else if (error.message) {
          // JavaScript error
          errorMessage = error.message;
        } else if (!error.response) {
          errorTitle = 'Erro de Conexão';
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
        }

        toastNotification('error', errorMessage, { title: errorTitle });
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutateTransactions, mutateJournalEntries, mutateStats]
  );

  // Unreconciliation operations
  const executeUnreconciliation = useCallback(
    async (reconciliationId, item) => {
      if (!reconciliationId) {
        throw new Error('ID de conciliação não fornecido');
      }

      try {
        console.log('[useReconciliationNew] Executing unreconciliation for:', reconciliationId);

        // Get user and company data from localStorage
        const userId = localStorage.getItem('userId') || '';

        if (!companyId) {
          throw new Error('Empresa não identificada');
        }

        const response = await removeReconciliation(reconciliationId, companyId, userId);

        console.log('[useReconciliationNew] Unreconciliation response:', response);
        console.log('[useReconciliationNew] Response structure:', {
          success: response.success,
          dataSuccess: response.data?.success,
          message: response.message,
          dataMessage: response.data?.message,
          fullResponse: response,
        });

        // Check if the response indicates success (API returns data with success property)
        if (response.data?.success || response.success) {
          toastNotification('success', 'Conciliação removida com sucesso!');

          // Refresh all relevant data with explicit cache invalidation (same pattern as reconciliation)
          console.log(
            '[useReconciliationNew] Refreshing data after successful unreconciliation...'
          );

          // Force complete cache invalidation and revalidation
          await Promise.all([
            mutateTransactions(null, { revalidate: true }), // Force fresh fetch
            mutateJournalEntries(null, { revalidate: true }), // Force fresh fetch
            mutateStats(null, { revalidate: true }), // Force fresh fetch
            mutateStatements(null, { revalidate: true }), // Force fresh fetch
            globalMutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-')), // Invalidate bank carousel
          ]);

          // Additional delay to ensure backend has processed the changes
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Second revalidation to ensure we have the latest data
          await Promise.all([
            mutateTransactions(), // Revalidate transactions
            mutateJournalEntries(), // Revalidate journal entries
            mutateStats(), // Revalidate stats
            mutateStatements(), // Revalidate statements
            globalMutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-')), // Refresh bank carousel
          ]);

          console.log(
            '[useReconciliationNew] Data refresh completed with forced cache invalidation after unreconciliation'
          );

          // Clear selections
          setSelectedTransactions([]);
          setSelectedJournalEntries([]);

          return { success: true };
        } else {
          const errorMessage =
            response.data?.message || response.message || 'Erro ao remover conciliação';
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error('Unreconciliation error:', error);
        console.error('Unreconciliation error details:', {
          message: error.message,
          response: error.response,
          data: error.response?.data,
          stack: error.stack,
        });

        let errorMessage = 'Erro inesperado durante a desconciliação';
        let errorTitle = 'Erro na Desconciliação';

        // Handle different error structures
        if (error.response?.data) {
          // API error response
          const errorData = error.response.data;
          errorMessage = errorData.message || errorMessage;

          if (error.response.status === 400) {
            errorTitle = 'Dados Inválidos';
            errorMessage =
              errorData.message || 'Os dados fornecidos para desconciliação são inválidos.';
          } else if (error.response.status === 404) {
            errorTitle = 'Conciliação Não Encontrada';
            errorMessage =
              'A conciliação selecionada não foi encontrada. Atualize a página e tente novamente.';
          }
        } else if (error.data?.message) {
          // Direct API response error
          errorMessage = error.data.message;
        } else if (error.message) {
          // JavaScript error
          errorMessage = error.message;
        } else if (!error.response) {
          errorTitle = 'Erro de Conexão';
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
        }

        toastNotification('error', errorMessage, { title: errorTitle });
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, mutateTransactions, mutateJournalEntries, mutateStats]
  );

  // Find automatic matches
  const findAutomaticMatches = useCallback(
    async (journalEntryIds) => {
      if (!selectedStatement?.id || !journalEntryIds?.length) {
        return [];
      }

      try {
        const response = await findMatchingTransactions({
          statementId: selectedStatement.id,
          journalEntryIds,
          companyId,
        });

        if (response.data?.success) {
          return response.data.data || [];
        } else {
          console.warn('No automatic matches found:', response.data?.message);
          return [];
        }
      } catch (error) {
        console.error('Error finding matches:', error);
        return [];
      }
    },
    [selectedStatement?.id, companyId]
  );

  // Delete statement
  const handleDeleteStatement = useCallback(
    async (statementId) => {
      if (!companyId) {
        toastNotification('error', 'Empresa não identificada. Faça login novamente.', { title: 'Erro de Autenticação' });
        return;
      }

      try {
        const transactionsResponse = await getStatementTransactions(statementId, {
          page: 1,
          limit: 10000,
        });

        const statementTransactions =
          transactionsResponse?.data?.data?.data || transactionsResponse?.data?.data || [];

        const hasReconciled = statementTransactions.some(
          (transaction) => transaction?.reconciliationData || transaction?.isReconciled === true
        );

        if (hasReconciled) {
          toastNotification(
            'warning',
            'Nao e possivel excluir um extrato que possui conciliacoes.'
          );
          return;
        }

        const response = await deleteStatement(statementId, companyId);

        if (response.data?.success) {
          toastNotification('success', 'Extrato excluído com sucesso!');

          // Refresh data and clear selection if it was the deleted statement
          await mutateStatements();
          await mutateStats();

          if (selectedStatement?.id === statementId) {
            setSelectedStatement(null);
            setSelectedTransactions([]);
            setSelectedJournalEntries([]);
          }

          return response.data;
        } else {
          throw new Error(response.data?.message || 'Erro ao excluir extrato');
        }
      } catch (error) {
        console.error('Delete statement error:', error);
        const errorMessage =
          error.response?.data?.message || error.message || 'Erro ao excluir extrato';
        toastNotification('error', errorMessage);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStatement?.id, mutateStatements, mutateStats]
  );

  // Bulk unreconcile all transactions for a statement
  const handleBulkUnreconcileStatement = useCallback(
    async (statementId) => {
      if (!companyId) {
        toastNotification('error', 'Empresa não identificada. Faça login novamente.', { title: 'Erro de Autenticação' });
        return;
      }

      const userId = localStorage.getItem('userId');

      try {
        const response = await unreconcileAllByStatement(statementId, companyId, userId || 'unknown-user');

        if (response.data?.success) {
          const { removedReconciliations, removedTransactions } = response.data.data || {};
          toastNotification(
            'success',
            `${removedReconciliations || 0} conciliação(ões) removida(s) (${removedTransactions || 0} transações).`,
            { title: 'Extrato desconciliado' }
          );

          await mutateStatements();
          await mutateStats();

          // Clear selection if the affected statement was selected
          if (selectedStatement?.id === statementId) {
            await mutateTransactions();
          }

          return response.data;
        } else {
          throw new Error(response.data?.message || 'Erro ao desconciliar extrato');
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || error.message || 'Erro ao desconciliar extrato';
        toastNotification('error', errorMessage);
        throw error;
      }
    },
    [companyId, selectedStatement?.id, mutateStatements, mutateStats, mutateTransactions]
  );

  // Selection management
  const toggleTransactionSelection = useCallback((transaction) => {
    setSelectedTransactions((prev) => {
      const isSelected = prev.some((t) => t.transactionHash === transaction.transactionHash);
      if (isSelected) {
        return prev.filter((t) => t.transactionHash !== transaction.transactionHash);
      } else {
        return [...prev, transaction];
      }
    });
  }, []);

  const toggleJournalEntrySelection = useCallback((journalEntry) => {
    setSelectedJournalEntries((prev) => {
      const isSelected = prev.some((j) => j.id === journalEntry.id);
      if (isSelected) {
        return prev.filter((j) => j.id !== journalEntry.id);
      } else {
        return [...prev, journalEntry];
      }
    });
  }, []);

  const clearSelections = useCallback(() => {
    setSelectedTransactions([]);
    setSelectedJournalEntries([]);
  }, []);

  // Calculated values with proper null checks
  const selectedTransactionTotal = useMemo(() => {
    return selectedTransactions.reduce((total, transaction) => {
      const amount = Math.abs(parseFloat(transaction.amount) || 0);
      return total + amount;
    }, 0);
  }, [selectedTransactions]);

  const selectedJournalEntryTotal = useMemo(() => {
    return selectedJournalEntries.reduce((total, entry) => {
      const value = Math.abs(parseFloat(entry.valueFinal ?? entry.value) || 0);
      return total + value;
    }, 0);
  }, [selectedJournalEntries]);

  const selectionDifference = useMemo(() => {
    // Ensure we have valid numbers before calculation
    const transactionTotal = isNaN(selectedTransactionTotal) ? 0 : selectedTransactionTotal;
    const journalTotal = isNaN(selectedJournalEntryTotal) ? 0 : selectedJournalEntryTotal;
    return Math.abs(transactionTotal) - Math.abs(journalTotal);
  }, [selectedTransactionTotal, selectedJournalEntryTotal]);

  const canReconcile = useMemo(() => {
    // Ensure we have valid selections and numbers
    const hasValidSelections = selectedTransactions.length > 0 && selectedJournalEntries.length > 0;
    const hasSingleTransaction = selectedTransactions.length === 1;
    const transactionTotal = isNaN(selectedTransactionTotal) ? 0 : selectedTransactionTotal;
    const journalTotal = isNaN(selectedJournalEntryTotal) ? 0 : selectedJournalEntryTotal;
    const difference = Math.abs(transactionTotal - journalTotal);

    // ✅ CRITICAL: Check if any selected items are already reconciled
    const hasAlreadyReconciledTransactions = selectedTransactions.some(
      (t) => t.isReconciled === true
    );
    const hasAlreadyReconciledJournalEntries = selectedJournalEntries.some((je) =>
      isReconciled(je)
    );

    if (hasAlreadyReconciledTransactions || hasAlreadyReconciledJournalEntries) {
      console.warn(
        '[useReconciliationNew] Blocking reconciliation: some items are already reconciled',
        {
          reconciledTransactions: selectedTransactions.filter((t) => t.isReconciled === true)
            .length,
          reconciledJournalEntries: selectedJournalEntries.filter((je) => isReconciled(je)).length,
        }
      );
      return false;
    }

    // Check if dates are in the same month for any combination
    let hasSameMonthDates = false;
    for (const entry of selectedJournalEntries) {
      for (const txn of selectedTransactions) {
        const entryDate = new Date(entry.dateVencimento || entry.dateCompetencia);
        const txnDate = new Date(txn.datePosted);
        
        const entryMonth = entryDate.getUTCMonth();
        const entryYear = entryDate.getUTCFullYear();
        const txnMonth = txnDate.getUTCMonth();
        const txnYear = txnDate.getUTCFullYear();
        
        if (entryMonth === txnMonth && entryYear === txnYear) {
          hasSameMonthDates = true;
          break;
        }
      }
      if (hasSameMonthDates) break;
    }

    // Check if values are close enough (within 10% or R$10)
    const maxTotal = Math.max(Math.abs(transactionTotal), Math.abs(journalTotal));
    const valueTolerance = Math.max(maxTotal * 0.10, 10.00); // 10% or R$10, whichever larger
    const valuesAreClose = difference <= valueTolerance;

    console.log('[useReconciliationNew] Reconciliation validation:', {
      hasValidSelections,
      hasSameMonthDates,
      valuesAreClose,
      difference,
      valueTolerance,
      transactionTotal,
      journalTotal
    });

    return (
      hasValidSelections &&
      hasSingleTransaction &&
      !hasAlreadyReconciledTransactions &&
      !hasAlreadyReconciledJournalEntries &&
      hasSameMonthDates &&
      valuesAreClose
    );
  }, [
    selectedTransactions,
    selectedJournalEntries,
    selectedTransactionTotal,
    selectedJournalEntryTotal,
  ]);

  // Refresh all data with cache clearing
  const refreshAllData = useCallback(async () => {
    console.log('🔄 [useReconciliationNew] Refreshing all data with complete cache clear...');

    try {
      // Force complete cache invalidation by passing null data and revalidate
      await Promise.all([
        mutateStatements(null, { revalidate: true }),
        mutateTransactions(null, { revalidate: true }),
        mutateJournalEntries(null, { revalidate: true }),
        mutateStats(null, { revalidate: true }),
      ]);

      console.log('🎯 First wave completed, waiting for backend sync...');

      // Wait a moment for backend to sync
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Second revalidation to ensure we have the latest data
      await Promise.all([
        mutateJournalEntries(),
        mutateTransactions(),
        mutateStatements(),
        mutateStats(),
      ]);

      console.log('🎉 All data refreshed successfully with complete cache invalidation');
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
    }
  }, [mutateStatements, mutateTransactions, mutateJournalEntries, mutateStats]);

  return {
    // Data
    statements,
    transactions,
    journalEntries,
    stats,

    // State
    selectedStatement,
    selectedTransactions,
    selectedJournalEntries,
    isUploading,
    uploadProgress,
    transactionTypeFilter,
    reconciliationFilter,

    // Loading states
    isLoadingStatements,
    isLoadingTransactions,
    isLoadingJournalEntries,
    isLoadingStats,

    // Data counts
    statementsTotal: statements?.total || 0, // Extract from the statements response
    transactionsPagination,

    // Calculated values
    selectedTransactionTotal,
    selectedJournalEntryTotal,
    selectionDifference,
    canReconcile,

    // Actions
    setSelectedStatement,
    handleFileUpload,
    handleReconciliation,
    executeUnreconciliation,
    handleDeleteStatement,
    handleBulkUnreconcileStatement,
    findAutomaticMatches,
    toggleTransactionSelection,
    toggleJournalEntrySelection,
    clearSelections,
    refreshAllData,
    setTransactionTypeFilter,
    setReconciliationFilter,
  };
};
